import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import {
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { ScheduleCalendarDisabledDay } from '@/components/appointments/ScheduleCalendarDisabledDay';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import InviteQuestionnaireModal, { InviteQuestionnaireOption } from '@/clients/onboarding/components/InviteQuestionnaireModal';
import {
  schedulePanelInputSx,
  schedulePanelLabelSx,
  schedulePanelSelectSx,
  schedulePanelTextareaSx,
  scheduleMobileInputSx,
  scheduleMobileSelectSx,
  scheduleMobileTextareaSx,
  scheduleResponsiveGroupSx,
  scheduleResponsiveLabelSx,
  scheduleResponsiveSectionSx,
  scheduleActionButtonSx,
  scheduleActionButtonCompactSx,
  scheduleCalendarPanelSx,
  scheduleCenterPanelSx,
  scheduleCenterSearchSx,
  scheduleCenterSelectedClientSx,
  scheduleCenterSubtitleSx,
  scheduleCenterTitleSx,
  scheduleLoadingDayTooltip,
  schedulePastDayTooltip,
  scheduleUnavailableDayTooltip,
} from '@/components/appointments/scheduleDialogStyles';
import { useScheduleAvailability } from '@/hooks/appointments';
import type { ScheduleBlockedDateRange } from '@/hooks/appointments/useScheduleAvailability';
import {
  createAppointment,
  getAppointmentById,
  updateAppointment,
  type AppointmentAvailableSlot,
} from '@/lib/api/appointments';
import { assignQuestionnaire } from '@/lib/api/questionnaires';
import type { Appointment } from '@/types';
import { getClientsDashboard } from '@/lib/api/clients';
import { Client as ClientType } from '@/types/client';
import {
  getNutritionistHostDisplay,
  isPastScheduleDay,
  isScheduleTimeSlotInPast,
} from '@/utils/appointments';
import {
  addMinutesToHHmm,
  formatAvailableSlotLabel,
  getClientConditionSummary,
  getClientDisplayName,
  getClientStatusLabel,
  getAppointmentSlotTimeHHmm,
  getAppointmentStartDate,
  getClientDisplayFromAppointment,
  getIanaTimeZone,
  getSchedulePrefillFromAppointment,
  mergeClientsById,
  onlinePlatformOptions,
  scheduleDurationOptions,
  weekDayLabels,
  type ScheduleMeetingType,
  type SchedulePlatform,
} from '@/utils/appointments/schedule-dialog-utils';
import { buildAppointmentUtcTimes } from '@/utils/appointments/appointment-timezone';
import { isSameClientCalendarDay } from '@/utils/appointments/public-booking-calendar';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { extractErrorMessage, showSuccessToast } from '@/utils/toast-utils';

export type ScheduleAppointmentDraftPayload = {
  title: string;
  description?: string;
  date: string;
  startTime: string;
  endTime: string;
  startTimeUtc?: string;
  endTimeUtc?: string;
  timezone: string;
  durationMinutes: number;
  appointmentType: 'online' | 'offline';
  location?: string | null;
};

type MobileScheduleStep = 'details' | 'when';

export interface ScheduleAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: { date?: Date; time?: string; durationMinutes?: number };
  defaultClientId?: string;
  defaultTitle?: string;
  defaultDescription?: string;
  /** When set, hides client search and shows this client (invite flow or client details). */
  lockedClient?: { id?: string; name: string; email: string };
  /** Saves selection without calling create API (e.g. before client record exists). */
  onScheduleDraft?: (payload: ScheduleAppointmentDraftPayload) => void;
  /** When set, loads GET /appointments/:id and reschedules via PUT /appointments/:id. */
  appointmentIdToEdit?: string | null;
  /** Date ranges to block in this picker before they are saved as time off. */
  blockedDateRanges?: ScheduleBlockedDateRange[];
  onAppointmentCreated?: () => void | Promise<void>;
  onAppointmentUpdated?: () => void | Promise<void>;
}

export const ScheduleAppointmentDialog: React.FC<ScheduleAppointmentDialogProps> = ({
  open,
  onClose,
  prefill,
  defaultClientId = '',
  defaultTitle = '',
  defaultDescription = '',
  lockedClient,
  onScheduleDraft,
  appointmentIdToEdit = null,
  blockedDateRanges = [],
  onAppointmentCreated,
  onAppointmentUpdated,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDraftMode = Boolean(onScheduleDraft);
  const isEditMode = Boolean(appointmentIdToEdit) && !isDraftMode;
  const editAppointmentId = appointmentIdToEdit ? String(appointmentIdToEdit) : '';
  const [mobileStep, setMobileStep] = useState<MobileScheduleStep>('details');
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);
  const [loadingAppointmentToEdit, setLoadingAppointmentToEdit] = useState(false);
  const [scheduleMonth, setScheduleMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AppointmentAvailableSlot | null>(null);
  const [selectedScheduleClientId, setSelectedScheduleClientId] = useState<string>('');
  const [scheduleClientSearch, setScheduleClientSearch] = useState('');
  const [isScheduleClientListOpen, setIsScheduleClientListOpen] = useState(false);
  const scheduleClientSearchRef = useRef<HTMLDivElement>(null);
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number>(30);
  const [selectedMeetingType, setSelectedMeetingType] = useState<ScheduleMeetingType>('online');
  const [selectedPlatform, setSelectedPlatform] = useState<SchedulePlatform>('google_meet');
  const [inPersonAddress, setInPersonAddress] = useState('');
  const [isScheduleCreating, setIsScheduleCreating] = useState(false);
  const [isAppointmentCreatedPopupOpen, setIsAppointmentCreatedPopupOpen] = useState(false);
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] = useState(false);
  const [selectedQuestionnaireIds, setSelectedQuestionnaireIds] = useState<string[]>([]);
  const [selectedQuestionnaireOptions, setSelectedQuestionnaireOptions] = useState<InviteQuestionnaireOption[]>([]);
  const [scheduleAppointmentTitle, setScheduleAppointmentTitle] = useState('');
  const [scheduleAppointmentDescription, setScheduleAppointmentDescription] = useState('');
  const [scheduleFieldErrors, setScheduleFieldErrors] = useState<{
    client?: string;
    title?: string;
  }>({});
  const [scheduleFormFeedback, setScheduleFormFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);
  const [createdAppointmentMeta, setCreatedAppointmentMeta] = useState<{
    clientName: string;
    titleLabel: string;
    descriptionLabel?: string;
    dateLabel: string;
    modeLabel: string;
    locationLabel: string;
  } | null>(null);
  const [clients, setClients] = useState<ClientType[]>([]);
  const [scheduleClientResultIds, setScheduleClientResultIds] = useState<string[]>([]);
  const [isScheduleClientSearchLoading, setIsScheduleClientSearchLoading] = useState(false);
  const [scheduleAvailabilityReady, setScheduleAvailabilityReady] = useState(false);
  const isClientLocked = Boolean(lockedClient) || isEditMode;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const resetQuestionnaireAttachmentState = useCallback(() => {
    setIsQuestionnaireModalOpen(false);
    setSelectedQuestionnaireIds([]);
    setSelectedQuestionnaireOptions([]);
  }, []);

  useEffect(() => {
    resetQuestionnaireAttachmentState();
  }, [appointmentIdToEdit, resetQuestionnaireAttachmentState]);

  useEffect(() => {
    if (!open) {
      resetQuestionnaireAttachmentState();
      setMobileStep('details');
    }
  }, [open, resetQuestionnaireAttachmentState]);

  useEffect(() => {
    if (!open) {
      setAppointmentToEdit(null);
      setLoadingAppointmentToEdit(false);
      return;
    }

    if (!appointmentIdToEdit) {
      setAppointmentToEdit(null);
      setLoadingAppointmentToEdit(false);
      return;
    }

    let cancelled = false;
    setLoadingAppointmentToEdit(true);

    const loadAppointment = async () => {
      try {
        const { appointment } = await getAppointmentById(appointmentIdToEdit);
        if (!cancelled) {
          setAppointmentToEdit(appointment);
          setScheduleFormFeedback(null);
        }
      } catch (error) {
        if (!cancelled) {
          setAppointmentToEdit(null);
          setScheduleFormFeedback({
            variant: 'error',
            title: 'Failed to load appointment details',
            message: extractErrorMessage(error),
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingAppointmentToEdit(false);
        }
      }
    };

    void loadAppointment();

    return () => {
      cancelled = true;
    };
  }, [open, appointmentIdToEdit]);

  useEffect(() => {
    if (!open) {
      setScheduleAvailabilityReady(false);
      return;
    }

    if (appointmentIdToEdit) {
      if (loadingAppointmentToEdit || !appointmentToEdit) {
        setScheduleAvailabilityReady(false);
        return;
      }
    }

    const editPrefill = appointmentToEdit ? getSchedulePrefillFromAppointment(appointmentToEdit) : null;
    const prefillDate = editPrefill?.date ?? prefill?.date;
    setScheduleMonth(startOfMonth(prefillDate ?? new Date()));
    setSelectedScheduleDate(prefillDate ?? null);
    setSelectedTimeSlot(editPrefill?.time ?? prefill?.time ?? null);
    setSelectedScheduleClientId(
      editPrefill?.clientId || lockedClient?.id || defaultClientId || '',
    );
    setScheduleClientSearch('');
    setIsScheduleClientListOpen(false);
    setSelectedDurationMinutes(editPrefill?.durationMinutes ?? prefill?.durationMinutes ?? 30);
    setScheduleAppointmentTitle(editPrefill?.title || defaultTitle || '');
    setScheduleAppointmentDescription(editPrefill?.description || defaultDescription || '');
    setScheduleFieldErrors({});
    setScheduleFormFeedback(null);
    setSelectedMeetingType(editPrefill?.meetingType ?? 'online');
    setSelectedPlatform('google_meet');
    setInPersonAddress(editPrefill?.inPersonAddress ?? '');
    resetQuestionnaireAttachmentState();
    setScheduleAvailabilityReady(true);

    return () => {
      setScheduleAvailabilityReady(false);
    };
  }, [open, appointmentIdToEdit, appointmentToEdit, loadingAppointmentToEdit, resetQuestionnaireAttachmentState]);

  useEffect(() => {
    if (!open) {
      setIsScheduleClientListOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || isClientLocked) {
      setIsScheduleClientSearchLoading(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(
      () => {
        const run = async () => {
          setIsScheduleClientSearchLoading(true);
          try {
            const response = await getClientsDashboard({
              status: 'all',
              search: scheduleClientSearch.trim() || undefined,
              page: 1,
              limit: 10,
            });
            if (cancelled) return;

            const resultClients = (response?.clients || []) as unknown as ClientType[];
            setClients((prev) => mergeClientsById(prev, resultClients));
            setScheduleClientResultIds(resultClients.map((client) => String(client.id)));
          } catch (error) {
            if (!cancelled) {
              console.error('Error searching clients for schedule:', error);
              setScheduleClientResultIds([]);
            }
          } finally {
            if (!cancelled) {
              setIsScheduleClientSearchLoading(false);
            }
          }
        };

        void run();
      },
      scheduleClientSearch.trim() ? 300 : 0,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [open, scheduleClientSearch, isClientLocked]);

  const editClientDisplay = useMemo(
    () => (isEditMode && appointmentToEdit ? getClientDisplayFromAppointment(appointmentToEdit) : null),
    [isEditMode, appointmentToEdit],
  );

  const clientsById = useMemo(
    () => new Map(clients.map((client) => [client.id.toString(), client])),
    [clients],
  );

  const selectedClientForSchedule = useMemo(() => {
    if (!selectedScheduleClientId) return undefined;
    return clientsById.get(selectedScheduleClientId);
  }, [clientsById, selectedScheduleClientId]);

  const filteredScheduleClients = useMemo(() => {
    return scheduleClientResultIds
      .map((clientId) => clientsById.get(clientId))
      .filter((client): client is ClientType => Boolean(client));
  }, [clientsById, scheduleClientResultIds]);

  const scheduleClientName = lockedClient
    ? lockedClient.name
    : editClientDisplay?.name
      ?? (selectedClientForSchedule ? getClientDisplayName(selectedClientForSchedule) : 'Client');
  const scheduleClientEmail =
    lockedClient?.email || editClientDisplay?.email || selectedClientForSchedule?.email || '';
  const scheduleClientLinkId = lockedClient?.id || selectedScheduleClientId || selectedClientForSchedule?.id || '';
  const scheduleClientNameParts = scheduleClientName.trim().split(/\s+/).filter(Boolean);
  const questionnaireClientFirstName =
    selectedClientForSchedule?.firstName || scheduleClientNameParts[0] || 'Client';
  const questionnaireClientLastName =
    selectedClientForSchedule?.lastName || scheduleClientNameParts.slice(1).join(' ');
  const nutritionistHost = getNutritionistHostDisplay();
  const clientRequirementMet = isDraftMode || Boolean(selectedScheduleClientId.trim()) || isClientLocked;

  const {
    loading: scheduleAvailabilityLoading,
    calendarDays,
    slotsForSelectedDate: availableScheduleSlots,
    calendarLoaded,
    scheduleTimezone,
    slotsLoading,
    isDayAvailable,
  } = useScheduleAvailability({
    enabled: open && scheduleAvailabilityReady,
    scheduleMonth,
    selectedDate: selectedScheduleDate,
    durationMinutes: selectedDurationMinutes,
    excludeAppointmentId: isEditMode ? editAppointmentId : undefined,
    originalAppointmentStartTimeMs: appointmentToEdit
      ? getAppointmentStartDate(appointmentToEdit).getTime()
      : undefined,
    blockedDateRanges,
  });
  const hasUnavailableBookableDayInMonth = calendarDays.some(
    (day) => isSameMonth(day, scheduleMonth) && !isPastScheduleDay(day) && !isDayAvailable(day),
  );
  const showAvailabilitySettingsAction =
    calendarLoaded && !scheduleAvailabilityLoading && hasUnavailableBookableDayInMonth;

  const handleOpenAvailabilitySettings = () => {
    onClose();
    navigate('/profile?tab=availability');
  };

  const slotDisplayTimezone = getIanaTimeZone();

  const editOriginalSlotTime = useMemo(() => {
    if (!appointmentToEdit) return null;
    return getAppointmentSlotTimeHHmm(appointmentToEdit, slotDisplayTimezone);
  }, [appointmentToEdit, slotDisplayTimezone]);

  const editOriginalSlotTimeRef = useRef(editOriginalSlotTime);
  editOriginalSlotTimeRef.current = editOriginalSlotTime;

  useEffect(() => {
    if (!selectedScheduleDate || !calendarLoaded) return;
    if (isDayAvailable(selectedScheduleDate)) return;

    setSelectedScheduleDate(null);
    setSelectedTimeSlot(null);
    setSelectedSlot(null);
  }, [calendarLoaded, isDayAvailable, selectedScheduleDate]);

  useEffect(() => {
    if (!selectedTimeSlot || slotsLoading) return;
    const match = availableScheduleSlots.find((slot) => slot.startTime === selectedTimeSlot);
    if (match) {
      setSelectedSlot(match);
      return;
    }
    if (isEditMode && editOriginalSlotTime && selectedTimeSlot === editOriginalSlotTime) {
      return;
    }
    if (!availableScheduleSlots.some((slot) => slot.startTime === selectedTimeSlot)) {
      setSelectedTimeSlot(null);
      setSelectedSlot(null);
    }
  }, [availableScheduleSlots, selectedTimeSlot, slotsLoading, isEditMode, editOriginalSlotTime]);

  /** Reschedule: keep the current slot selected so meeting-type-only edits can be saved. */
  useEffect(() => {
    if (!isEditMode || !appointmentToEdit || !selectedScheduleDate || slotsLoading) {
      return;
    }
    const originalSlotTime = editOriginalSlotTimeRef.current;
    if (!originalSlotTime) return;

    const originalStart = getAppointmentStartDate(appointmentToEdit);
    if (!isSameClientCalendarDay(selectedScheduleDate, originalStart, slotDisplayTimezone)) {
      return;
    }

    const match = availableScheduleSlots.find((slot) => slot.startTime === originalSlotTime);
    if (!match) return;

    const selectionMissing = !selectedTimeSlot;
    const selectionInvalid =
      Boolean(selectedTimeSlot) &&
      !availableScheduleSlots.some((slot) => slot.startTime === selectedTimeSlot);

    if (selectionMissing || selectionInvalid) {
      setSelectedTimeSlot(originalSlotTime);
      setSelectedSlot(match);
    }
  }, [isEditMode, appointmentToEdit, selectedScheduleDate, slotsLoading, availableScheduleSlots, selectedTimeSlot]);

  useEffect(() => {
    if (!isScheduleClientListOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (scheduleClientSearchRef.current?.contains(target)) return;
      setIsScheduleClientListOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsScheduleClientListOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isScheduleClientListOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleCloseCreatedPopup = () => {
    setIsAppointmentCreatedPopupOpen(false);
    setCreatedAppointmentMeta(null);
  };

  const handleOpenQuestionnaireModal = () => {
    if (!scheduleClientLinkId) {
      setScheduleFieldErrors((current) => ({
        ...current,
        client: 'Please select a client before attaching questionnaires.',
      }));
      setScheduleFormFeedback(null);
      return;
    }

    setIsQuestionnaireModalOpen(true);
  };

  const handleAttachQuestionnaires = (questionnaireIds: string[], selectedOptions?: InviteQuestionnaireOption[]) => {
    setSelectedQuestionnaireIds(questionnaireIds);
    setSelectedQuestionnaireOptions(selectedOptions ?? []);
    setIsQuestionnaireModalOpen(false);
  };

  const handleDatePick = (day: Date) => {
    const isOutsideCurrentMonth = !isSameMonth(day, scheduleMonth);
    if (isOutsideCurrentMonth) {
      setScheduleMonth(startOfMonth(day));
    }

    if (isPastScheduleDay(day)) return;
    if (!scheduleAvailabilityLoading && !isDayAvailable(day)) return;

    setSelectedScheduleDate(day);
    setSelectedTimeSlot(null);
    setSelectedSlot(null);
    setScheduleFormFeedback(null);
  };

  const canContinueFromDetails = Boolean(
    scheduleAppointmentTitle.trim() && clientRequirementMet,
  );
  const canSubmitAppointment = Boolean(
    selectedScheduleDate &&
      selectedTimeSlot &&
      clientRequirementMet &&
      scheduleAppointmentTitle.trim() &&
      !isScheduleCreating,
  );

  const handleMobileContinue = () => {
    if (!scheduleAppointmentTitle.trim()) {
      setScheduleFieldErrors((current) => ({
        ...current,
        title: 'Appointment name is required.',
      }));
      return;
    }
    if (!clientRequirementMet) {
      setScheduleFieldErrors((current) => ({
        ...current,
        client: 'Please select a client to continue.',
      }));
      return;
    }
    setScheduleFieldErrors((current) => ({ ...current, title: undefined, client: undefined }));
    setScheduleFormFeedback(null);
    setMobileStep('when');
  };

  const mobileWhenSummary = [
    scheduleClientName !== 'Client' ? scheduleClientName : null,
    `${selectedDurationMinutes} min`,
    selectedMeetingType === 'in_person' ? 'In-person' : 'Online',
  ]
    .filter(Boolean)
    .join(' · ');

  const handleContinueAppointment = async () => {
    if (!selectedScheduleDate || !selectedTimeSlot) {
      setScheduleFormFeedback({
        variant: 'error',
        title: 'Date and time required',
        message: 'Select an available date and time before continuing.',
      });
      return;
    }
    if (!isDraftMode && !selectedScheduleClientId.trim()) {
      setScheduleFieldErrors((current) => ({ ...current, client: 'Please select a client before continuing.' }));
      setScheduleFormFeedback(null);
      return;
    }

    const appointmentTitle = scheduleAppointmentTitle.trim();
    if (!appointmentTitle) {
      setScheduleFieldErrors((current) => ({ ...current, title: 'Please enter an appointment name.' }));
      setScheduleFormFeedback(null);
      return;
    }
    if (isPastScheduleDay(selectedScheduleDate)) {
      setScheduleFormFeedback({
        variant: 'error',
        title: 'Invalid date',
        message: 'Cannot schedule appointments in the past.',
      });
      return;
    }
    if (isScheduleTimeSlotInPast(selectedTimeSlot, selectedScheduleDate, slotDisplayTimezone)) {
      setScheduleFormFeedback({
        variant: 'error',
        title: 'Invalid time',
        message: 'Cannot schedule appointments in the past.',
      });
      return;
    }
    setScheduleFieldErrors({});
    setScheduleFormFeedback(null);
    const appointmentDescription = scheduleAppointmentDescription.trim();

    const selectedPlatformLabel =
      onlinePlatformOptions.find((platform) => platform.id === selectedPlatform)?.label || 'Google Meet';
    const isInPerson = selectedMeetingType === 'in_person';
    const startTimeStr = selectedSlot?.startTime ?? selectedTimeSlot;
    const endTimeStr =
      selectedSlot?.endTime ?? addMinutesToHHmm(startTimeStr, selectedDurationMinutes);
    const formattedDate = format(selectedScheduleDate, 'yyyy-MM-dd');
    const timezone = slotDisplayTimezone;
    const utcTimes = buildAppointmentUtcTimes({
      date: formattedDate,
      startTime: startTimeStr,
      endTime: endTimeStr,
      timezone,
      startTimeUtc: selectedSlot?.startTimeUtc,
      endTimeUtc: selectedSlot?.endTimeUtc,
    });

    if (isDraftMode && onScheduleDraft) {
      onScheduleDraft({
        title: appointmentTitle,
        ...(appointmentDescription ? { description: appointmentDescription } : {}),
        date: formattedDate,
        startTime: startTimeStr,
        endTime: endTimeStr,
        ...(selectedSlot?.startTimeUtc ? { startTimeUtc: selectedSlot.startTimeUtc } : {}),
        ...(selectedSlot?.endTimeUtc ? { endTimeUtc: selectedSlot.endTimeUtc } : {}),
        timezone,
        durationMinutes: selectedDurationMinutes,
        appointmentType: isInPerson ? 'offline' : 'online',
        location: isInPerson ? (inPersonAddress.trim() || null) : null,
      });
      showSuccessToast('Appointment scheduled', 'Appointment details saved for this invite.');
      onClose();
      return;
    }

    setIsScheduleCreating(true);
    try {
      if (isEditMode && editAppointmentId) {
        await updateAppointment(editAppointmentId, {
          clientId: selectedScheduleClientId,
          title: appointmentTitle,
          ...(appointmentDescription ? { description: appointmentDescription } : {}),
          startTime: utcTimes.startTime,
          endTime: utcTimes.endTime,
          appointmentType: isInPerson ? 'offline' : 'online',
          location: isInPerson ? (inPersonAddress.trim() || undefined) : undefined,
          updateScope: 'this_only',
        });

        if (selectedQuestionnaireIds.length > 0) {
          await Promise.all(
            selectedQuestionnaireIds.map((questionnaireId) =>
              assignQuestionnaire(questionnaireId, {
                clients: [{ clientId: selectedScheduleClientId, sendEmail: true }],
              }),
            ),
          );
        }

        showSuccessToast('Appointment updated', 'The session has been rescheduled.');
        resetQuestionnaireAttachmentState();
        onClose();
        await onAppointmentUpdated?.();
      } else {
        await createAppointment({
          clientId: selectedScheduleClientId,
          title: appointmentTitle,
          ...(appointmentDescription ? { description: appointmentDescription } : {}),
          startTime: utcTimes.startTime,
          endTime: utcTimes.endTime,
          appointmentType: isInPerson ? 'offline' : 'online',
          location: isInPerson ? (inPersonAddress.trim() || null) : null,
          meetingLink: null,
          additionalAttendees: [],
        });

        if (selectedQuestionnaireIds.length > 0) {
          await Promise.all(
            selectedQuestionnaireIds.map((questionnaireId) =>
              assignQuestionnaire(questionnaireId, {
                clients: [{ clientId: selectedScheduleClientId, sendEmail: true }],
              }),
            ),
          );
        }

        setCreatedAppointmentMeta({
          clientName: scheduleClientName,
          titleLabel: appointmentTitle,
          ...(appointmentDescription ? { descriptionLabel: appointmentDescription } : {}),
          dateLabel: format(selectedScheduleDate, 'dd MMM yyyy'),
          modeLabel: isInPerson ? 'In-Person' : 'Video Call',
          locationLabel: isInPerson ? (inPersonAddress.trim() || 'Clinic Address') : selectedPlatformLabel,
        });

        showSuccessToast('Appointment created', 'The session has been added to your calendar.');
        onClose();
        setIsAppointmentCreatedPopupOpen(true);
        await onAppointmentCreated?.();
      }
    } catch (error) {
      setScheduleFormFeedback({
        variant: 'error',
        title: isEditMode ? 'Failed to update appointment' : 'Failed to create appointment',
        message: extractErrorMessage(error),
      });
    } finally {
      setIsScheduleCreating(false);
    }
  };

  const renderTimesPanel = (placement: 'inline' | 'sidebar') => (
    <Box
      data-demo-id="appointment-scheduler-times"
      sx={{
        borderLeft: placement === 'sidebar' ? { xs: 'none', md: '1px solid #EAF0F5' } : 'none',
        borderTop: placement === 'inline' ? '1px solid #E4E7EC' : { xs: 'none', md: 'none' },
        bgcolor: placement === 'inline' ? '#F8FAFC' : { xs: '#F8FAFC', md: '#FBFDFF' },
        display:
          placement === 'inline'
            ? { xs: mobileStep === 'when' ? 'flex' : 'none', md: 'none' }
            : { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        flexShrink: placement === 'inline' ? 0 : undefined,
        minHeight: placement === 'sidebar' ? { xs: 'auto', md: 0 } : 'auto',
        height: placement === 'sidebar' ? { xs: 'auto', md: '100%' } : 'auto',
        position: 'relative',
        mt: placement === 'inline' ? 1.5 : { xs: 1.5, md: 0 },
        mx: placement === 'inline' ? 1.75 : 0,
        px: placement === 'inline' ? 1.5 : { xs: 1.75, md: 0 },
        pt: placement === 'inline' ? 1.5 : { xs: 1.75, md: 0 },
        pb: placement === 'inline' ? 1.5 : { xs: 3, md: 0 },
        borderRadius: placement === 'inline' ? '16px' : 0,
        boxSizing: 'border-box',
        width: placement === 'inline' ? 'auto' : '100%',
        alignSelf: placement === 'sidebar' ? { xs: 'start', md: 'stretch' } : 'stretch',
      }}
    >
      <Box
        sx={{
          px: { xs: 0, md: 2 },
          pt: { xs: 0, md: 2.2 },
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedScheduleDate ? (
            <>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  color: '#1D2939',
                }}
              >
                {format(selectedScheduleDate, 'EEE')} · {format(selectedScheduleDate, 'd MMM')}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.78rem',
                  color: '#98A2B3',
                }}
              >
                {slotsLoading
                  ? 'Loading available times…'
                  : `${availableScheduleSlots.length} slot${availableScheduleSlots.length === 1 ? '' : 's'} available · ${selectedDurationMinutes} min each`}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '0.95rem', md: '1rem' },
                color: '#1D2939',
              }}
            >
              Available times
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            flexShrink: 0,
            width: 36,
            height: 36,
            bgcolor: '#2CA8D8',
            color: '#fff',
            '&:hover': { bgcolor: '#249AC8' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      {selectedScheduleDate ? (
        <Box
          sx={{
            px: { xs: 0, md: 1.5 },
            pb: { xs: 0, md: 1.5 },
            overflowY: placement === 'sidebar' ? { xs: 'visible', md: 'auto' } : 'visible',
            flex: placement === 'sidebar' ? { xs: 'none', md: 1 } : 'none',
            minHeight: 0,
          }}
        >
          {slotsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} sx={{ color: '#2FA8D5' }} />
            </Box>
          ) : availableScheduleSlots.length === 0 ? (
            <Typography
              sx={{
                py: 2,
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '0.86rem',
                color: '#98A2B3',
              }}
            >
              No available times on this day
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: '1fr' },
                gap: { xs: 1, md: 0.75 },
                pt: placement === 'inline' ? 0.25 : 0,
              }}
            >
              {availableScheduleSlots.map((slot) => {
                const isSelected = selectedTimeSlot === slot.startTime;

                const isOriginalSlot =
                  isEditMode &&
                  Boolean(editOriginalSlotTime) &&
                  slot.startTime === editOriginalSlotTime &&
                  Boolean(selectedScheduleDate) &&
                  isSameDay(getAppointmentStartDate(appointmentToEdit!), selectedScheduleDate);

                return (
                  <Button
                    key={`${placement}-${slot.startTime}-${slot.endTime}`}
                    onClick={() => {
                      setSelectedTimeSlot(slot.startTime);
                      setSelectedSlot(slot);
                      setScheduleFormFeedback(null);
                    }}
                    sx={{
                      minHeight: { xs: 48, md: 40 },
                      py: { xs: 1.25, md: 0.75 },
                      px: { xs: 1.5, md: 1.25 },
                      borderRadius: '14px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: { xs: '0.86rem', md: '0.92rem' },
                      border: '1px solid',
                      borderColor: isOriginalSlot && !isSelected ? '#B9E6FE' : '#D7E1EB',
                      color: isSelected ? '#fff' : isOriginalSlot ? '#026AA2' : '#475467',
                      bgcolor: isSelected ? '#2FA8D5' : isOriginalSlot ? '#F0F9FF' : '#fff',
                      '&:hover': {
                        bgcolor: isSelected ? '#249CCB' : isOriginalSlot ? '#E0F2FE' : '#F7FAFD',
                      },
                    }}
                  >
                    {formatAvailableSlotLabel(slot)}
                    {isOriginalSlot && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.75rem',
                          ml: 1,
                          fontWeight: 600,
                          color: isSelected ? 'rgba(255,255,255,0.9)' : '#026AA2',
                        }}
                      >
                        (Current)
                      </Box>
                    )}
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: placement === 'sidebar' ? { xs: 'none', md: 1 } : 'none',
            px: { xs: 0, md: 2 },
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 120, md: 0 },
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                borderRadius: '16px',
                bgcolor: '#EAF0F5',
                color: '#9DAAB8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccessTimeRoundedIcon />
            </Box>
            <Typography
              sx={{
                mt: 1.2,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '0.86rem',
                lineHeight: 1.35,
                color: '#98A2B3',
              }}
            >
              {showAvailabilitySettingsAction
                ? 'Add availability slots, then select an enabled date to view times.'
                : 'Select an enabled date to view available times.'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          'data-demo-id': 'appointment-scheduler-dialog',
          sx: {
            width: { xs: '100%', md: 'min(1180px, calc(100vw - 56px))' },
            height: { xs: '100dvh', md: 'min(760px, calc(100vh - 40px))' },
            maxHeight: { xs: '100dvh', md: 'calc(100vh - 40px)' },
            borderRadius: { xs: 0, md: '22px' },
            overflow: 'hidden',
            m: 0,
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.35,
              borderBottom: '1px solid #EAF0F5',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: '#1D2939',
                  }}
                >
                  {isEditMode ? 'Reschedule Session' : 'Schedule Session'}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.2,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.78rem',
                    color: '#667085',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mobileStep === 'when' && mobileWhenSummary
                    ? mobileWhenSummary
                    : 'Client, session details, then pick a time'}
                </Typography>
              </Box>
              <IconButton
                onClick={handleClose}
                aria-label="Close"
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#2CA8D8',
                  color: '#fff',
                  '&:hover': { bgcolor: '#249AC8' },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
            <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
              {(
                [
                  { key: 'details', label: '1 · Details' },
                  { key: 'when', label: '2 · When' },
                ] as const
              ).map((step) => {
                const isActive = mobileStep === step.key;
                const isComplete = mobileStep === 'when' && step.key === 'details';
                return (
                  <Box
                    key={step.key}
                    sx={{
                      px: 1,
                      py: 0.7,
                      borderRadius: '10px',
                      textAlign: 'center',
                      bgcolor: isActive || isComplete ? '#EAF6FD' : '#F2F4F7',
                      border: `1px solid ${isActive || isComplete ? '#B9E6FE' : '#E4E7EC'}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: isActive || isComplete ? '#026AA2' : '#98A2B3',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}
        <Box
          sx={{
            position: 'relative',
            display: { xs: 'flex', md: 'grid' },
            flexDirection: { xs: 'column', md: undefined },
            gridTemplateColumns: {
              md: '340px minmax(0, 1fr) 220px',
            },
            alignContent: { md: 'stretch' },
            alignItems: { xs: 'stretch', md: 'stretch' },
            flex: 1,
            minHeight: 0,
            height: { md: '100%' },
            overflowY: { xs: 'auto', md: 'hidden' },
            WebkitOverflowScrolling: 'touch',
            bgcolor: {
              xs: '#F8FAFC',
              md: 'transparent',
            },
            background: {
              xs: 'none',
              md: 'none',
            },
          }}
        >
          {loadingAppointmentToEdit ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.82)',
              }}
            >
              <CircularProgress size={36} sx={{ color: '#2FA8D5' }} />
            </Box>
          ) : null}
          <Box
            data-demo-id="appointment-scheduler-session"
            sx={{
              display: {
                xs: mobileStep === 'details' ? 'block' : 'none',
                md: 'block',
              },
              order: { xs: 2, md: 0 },
              width: { xs: '100%', md: 'auto' },
              boxSizing: 'border-box',
              px: { xs: 1.75, md: 2.2 },
              pt: { xs: 0.5, md: 2.3 },
              pb: { xs: 2, md: 2.3 },
              bgcolor: { xs: 'transparent', md: '#1184B1' },
              background: {
                xs: 'none',
                md: 'linear-gradient(180deg, #0F8AB5 0%, #1AA3D0 55%, #2AA9D7 100%)',
              },
              color: { xs: '#1D2939', md: '#fff' },
              overflowY: { xs: 'visible', md: 'auto' },
              minHeight: 0,
              height: 'auto',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(226, 244, 252, 0.95)',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {isEditMode ? 'Reschedule' : 'New Appointment'}
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '1.35rem', md: '1.6rem' },
                lineHeight: 1.1,
                color: '#fff',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {isEditMode ? 'Reschedule Session' : 'Schedule Session'}
            </Typography>

            <Box sx={scheduleResponsiveSectionSx}>
              <Typography sx={{ ...scheduleResponsiveLabelSx, display: { xs: 'none', md: 'block' } }}>
                Session details
              </Typography>
              <Box sx={scheduleResponsiveGroupSx}>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={scheduleResponsiveLabelSx}>Appointment Name</Typography>
                  <TextField
                    inputProps={{ 'data-demo-id': 'appointment-scheduler-title' }}
                    value={scheduleAppointmentTitle}
                    onChange={(event) => {
                      setScheduleAppointmentTitle(event.target.value);
                      setScheduleFieldErrors((current) => ({ ...current, title: undefined }));
                      setScheduleFormFeedback(null);
                    }}
                    placeholder="Initial Consultation"
                    fullWidth
                    size="small"
                    error={Boolean(scheduleFieldErrors.title)}
                    helperText={scheduleFieldErrors.title}
                    sx={[
                      { mt: 0.75, width: '100%' },
                      schedulePanelInputSx,
                      (theme) => theme.breakpoints.down('md') ? scheduleMobileInputSx : {},
                    ]}
                  />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={scheduleResponsiveLabelSx}>Description</Typography>
                  <TextField
                    inputProps={{ 'data-demo-id': 'appointment-scheduler-description' }}
                    value={scheduleAppointmentDescription}
                    onChange={(event) => setScheduleAppointmentDescription(event.target.value)}
                    placeholder="Add notes or agenda (optional)"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={3}
                    sx={[
                      { mt: 0.75, width: '100%' },
                      schedulePanelTextareaSx,
                      (theme) => theme.breakpoints.down('md') ? scheduleMobileTextareaSx : {},
                    ]}
                  />
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={scheduleResponsiveLabelSx}>Duration</Typography>
                  <TextField
                    inputProps={{ 'data-demo-id': 'appointment-scheduler-duration' }}
                    select
                    fullWidth
                    size="small"
                    value={selectedDurationMinutes}
                    onChange={(event) => setSelectedDurationMinutes(Number(event.target.value))}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    sx={[
                      { mt: 0.75, width: '100%' },
                      schedulePanelSelectSx,
                      (theme) => theme.breakpoints.down('md') ? scheduleMobileSelectSx : {},
                    ]}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeRoundedIcon
                            sx={{ color: { xs: '#667085', md: '#d9eef7' }, fontSize: 16 }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {scheduleDurationOptions.map((minutes) => (
                      <MenuItem key={minutes} value={minutes}>
                        {minutes} minutes
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Typography sx={scheduleResponsiveLabelSx}>Meeting Type</Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      p: 0.28,
                      borderRadius: '14px',
                      bgcolor: { xs: '#F2F4F7', md: 'rgba(255,255,255,0.2)' },
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0.32,
                    }}
                  >
                <Button
                  data-demo-id="appointment-scheduler-online"
                  disableElevation
                  onClick={() => setSelectedMeetingType('online')}
                  sx={{
                    minHeight: 34,
                    borderRadius: '13px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    bgcolor: selectedMeetingType === 'online' ? '#fff' : 'transparent',
                    color: {
                      xs: selectedMeetingType === 'online' ? '#026AA2' : '#667085',
                      md: selectedMeetingType === 'online' ? '#0F3042' : 'rgba(226, 244, 252, 0.9)',
                    },
                    boxShadow: selectedMeetingType === 'online' ? { xs: '0 1px 2px rgba(16,24,40,0.06)', md: 'none' } : 'none',
                    '&:hover': {
                      bgcolor: {
                        xs: selectedMeetingType === 'online' ? '#fff' : '#E4E7EC',
                        md: selectedMeetingType === 'online' ? '#fff' : 'rgba(255,255,255,0.08)',
                      },
                    },
                  }}
                >
                  Online
                </Button>
                <Button
                  data-demo-id="appointment-scheduler-in-person"
                  disableElevation
                  onClick={() => setSelectedMeetingType('in_person')}
                  sx={{
                    minHeight: 34,
                    borderRadius: '13px',
                    textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    bgcolor: selectedMeetingType === 'in_person' ? '#fff' : 'transparent',
                    color: {
                      xs: selectedMeetingType === 'in_person' ? '#026AA2' : '#667085',
                      md: selectedMeetingType === 'in_person' ? '#0F3042' : 'rgba(226, 244, 252, 0.9)',
                    },
                    boxShadow: selectedMeetingType === 'in_person' ? { xs: '0 1px 2px rgba(16,24,40,0.06)', md: 'none' } : 'none',
                    '&:hover': {
                      bgcolor: {
                        xs: selectedMeetingType === 'in_person' ? '#fff' : '#E4E7EC',
                        md: selectedMeetingType === 'in_person' ? '#fff' : 'rgba(255,255,255,0.08)',
                      },
                    },
                  }}
                >
                  In-Person
                </Button>
                  </Box>
                </Box>
                {selectedMeetingType === 'in_person' ? (
                  <Box sx={{ width: '100%' }}>
                    <Typography sx={scheduleResponsiveLabelSx}>Location/Notes</Typography>
                    <TextField
                      value={inPersonAddress}
                      onChange={(event) => setInPersonAddress(event.target.value)}
                      placeholder="Enter location or notes..."
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      sx={[
                        { mt: 0.75, width: '100%' },
                        schedulePanelTextareaSx,
                        (theme) => theme.breakpoints.down('md') ? scheduleMobileTextareaSx : {},
                        {
                          '& .MuiInputBase-root': {
                            minHeight: 56,
                            bgcolor: { xs: '#fff', md: '#fff' },
                            color: { xs: '#1D2939', md: '#0F3042' },
                          },
                          '& .MuiInputBase-input': {
                            color: { xs: '#1D2939', md: '#0F3042' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: { xs: '#98A2B3', md: '#7A8894' },
                            opacity: 1,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: { xs: 'transparent', md: '#D8EEF7' },
                          },
                          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: { xs: 'transparent', md: '#B9E4F4' },
                          },
                          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: { xs: 'transparent', md: '#8ED6F1' },
                          },
                        },
                      ]}
                    />
                  </Box>
                ) : null}
              </Box>
            </Box>

            {/* Video platform picker (Google Meet, Zoom, Teams) — hidden for now
            {selectedMeetingType === 'online' ? (
              <Box sx={{ mt: 1.45 }}>
                {(() => {
                  const selectedOption = onlinePlatformOptions.find((platform) => platform.id === selectedPlatform);
                  const otherOptions = onlinePlatformOptions.filter((platform) => platform.id !== selectedPlatform);

                  return (
                    <>
                      {selectedOption && (
                        <>
                          <Button
                            onClick={() => setSelectedPlatform(selectedOption.id)}
                            sx={{
                              width: '100%',
                              px: 0.9,
                              py: 0.76,
                              minHeight: 61,
                              borderRadius: '14px',
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              bgcolor: 'rgba(255,255,255,0.18)',
                              border: '1px solid rgba(181, 235, 255, 0.56)',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.22)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '13px',
                                bgcolor: '#ffffff',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                component="img"
                                src={selectedOption.icon}
                                alt={selectedOption.iconAlt}
                                sx={{
                                  width: selectedOption.id === 'google_meet' ? 38 : 34,
                                  height: selectedOption.id === 'google_meet' ? 38 : 34,
                                  objectFit: 'contain',
                                  display: 'block',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                ml: 0.96,
                                flex: 1,
                                textAlign: 'left',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 700,
                                fontSize: '0.84rem',
                                color: '#EAF8FF',
                              }}
                            >
                              {selectedOption.label}
                            </Typography>
                            <Box
                              sx={{
                                width: 27,
                                height: 27,
                                borderRadius: '999px',
                                bgcolor: 'rgba(120, 208, 236, 0.45)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#EAF8FF',
                              }}
                            >
                              <CheckRoundedIcon sx={{ fontSize: 15 }} />
                            </Box>
                          </Button>
                          <Typography
                            sx={{
                              mt: 1,
                              ml: 0.15,
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: '#EAF8FF',
                            }}
                          >
                            {selectedOption.label}
                          </Typography>
                        </>
                      )}

                      <Box sx={{ mt: 1, display: 'grid', gap: 0.8 }}>
                        {otherOptions.map((platform) => (
                          <Button
                            key={platform.id}
                            onClick={() => setSelectedPlatform(platform.id)}
                            sx={{
                              px: 0.9,
                              py: 0.76,
                              minHeight: 53,
                              borderRadius: '11px',
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              bgcolor: 'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(181, 235, 255, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.12)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 37,
                                height: 37,
                                borderRadius: '10px',
                                bgcolor: '#ffffff',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                component="img"
                                src={platform.icon}
                                alt={platform.iconAlt}
                                sx={{
                                  width: platform.id === 'google_meet' ? 35 : 30,
                                  height: platform.id === 'google_meet' ? 35 : 30,
                                  objectFit: 'contain',
                                  display: 'block',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                ml: 0.92,
                                flex: 1,
                                textAlign: 'left',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                color: 'rgba(184, 229, 245, 0.95)',
                              }}
                            >
                              {platform.label}
                            </Typography>
                          </Button>
                        ))}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : (
            */}

            <Box
              sx={{
                mt: { xs: 1.5, md: 2.25 },
                pt: { xs: 0, md: 1.75 },
                borderTop: {
                  xs: 'none',
                  md: '1px solid rgba(181, 235, 255, 0.25)',
                },
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography sx={schedulePanelLabelSx}>Host</Typography>
              <Typography
                sx={{
                  mt: 0.52,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: '#fff',
                }}
              >
                {nutritionistHost.name}
              </Typography>
              {nutritionistHost.email ? (
                <Typography
                  sx={{
                    mt: 0.12,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: 'rgba(226, 244, 252, 0.78)',
                  }}
                >
                  {nutritionistHost.email}
                </Typography>
              ) : null}

              {selectedClientForSchedule || lockedClient ? (
                <>
                  <Typography
                    sx={{
                      mt: 2,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '12px',
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: 'rgba(226, 244, 252, 0.95)',
                    }}
                  >
                    Client
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.52,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: '#fff',
                    }}
                  >
                    <ClientNameLink clientId={scheduleClientLinkId} className="text-white hover:text-white focus-visible:text-white">
                      {scheduleClientName}
                    </ClientNameLink>
                  </Typography>
                  {scheduleClientEmail ? (
                    <Typography
                      sx={{
                        mt: 0.12,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        color: 'rgba(226, 244, 252, 0.78)',
                      }}
                    >
                      {scheduleClientEmail}
                    </Typography>
                  ) : null}
                </>
              ) : null}
              </Box>

              {!isDraftMode ? (
                <Box>
                  <Button
                    data-demo-id="appointment-scheduler-questionnaires"
                    disableElevation
                    startIcon={<AssignmentRoundedIcon sx={{ fontSize: 18 }} />}
                    onClick={handleOpenQuestionnaireModal}
                    sx={{
                      mt: { xs: 1.5, md: 2 },
                      width: '100%',
                      minHeight: 44,
                      borderRadius: '14px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      bgcolor: {
                        xs: selectedQuestionnaireIds.length > 0 ? '#EAF8FF' : '#F8FAFC',
                        md: selectedQuestionnaireIds.length > 0 ? '#EAF8FF' : '#fff',
                      },
                      color: '#0F3042',
                      border: { xs: '1px solid #E4E7EC', md: 'none' },
                      boxShadow: { xs: 'none', md: '0 10px 24px rgba(15, 48, 66, 0.18)' },
                      '&:hover': {
                        bgcolor: selectedQuestionnaireIds.length > 0 ? '#DDF1FB' : { xs: '#F2F4F7', md: '#F5FAFF' },
                        boxShadow: { xs: 'none', md: '0 12px 28px rgba(15, 48, 66, 0.22)' },
                      },
                      '&.Mui-disabled': {
                        bgcolor: { xs: '#F2F4F7', md: 'rgba(255,255,255,0.62)' },
                        color: 'rgba(15, 48, 66, 0.54)',
                      },
                    }}
                  >
                    {selectedQuestionnaireIds.length > 0 
                      ? `Attached Questionnaires (${selectedQuestionnaireIds.length})`
                      : 'Attach Questionnaires'}
                  </Button>
                  
                  {selectedQuestionnaireOptions.length > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {selectedQuestionnaireOptions.map((q) => (
                        <Box
                          key={q.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.75,
                            bgcolor: { xs: '#F0F9FF', md: 'rgba(255, 255, 255, 0.1)' },
                            border: { xs: '1px solid #B9E6FE', md: 'none' },
                            borderRadius: '8px',
                            px: 1.25,
                            py: 0.85,
                          }}
                        >
                          <AssignmentRoundedIcon
                            sx={{
                              fontSize: 14,
                              color: { xs: '#026AA2', md: 'rgba(226, 244, 252, 0.9)' },
                              mt: 0.2,
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.8rem',
                              lineHeight: 1.3,
                              color: { xs: '#1D2939', md: '#fff' },
                            }}
                          >
                            {q.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : null}
            </Box>
          </Box>

            <Box
              sx={{
                display: {
                  xs: mobileStep === 'details' || mobileStep === 'when' ? 'flex' : 'none',
                  md: 'grid',
                },
                flexDirection: { xs: 'column', md: undefined },
                order: { xs: mobileStep === 'details' ? 1 : 0, md: 0 },
                gridTemplateRows: {
                  md: 'auto minmax(0, 1fr) auto',
                },
                minWidth: 0,
                height: { xs: 'auto', md: '100%' },
                minHeight: 0,
                alignSelf: { xs: 'start', md: 'stretch' },
                width: '100%',
              }}
            >
            <Box
              sx={{
                position: 'relative',
                zIndex: { xs: 0, md: 4 },
                px: { xs: 1.75, md: 2.5 },
                pt: { xs: 1.5, md: 2 },
                pb: { xs: 0, md: 2 },
                borderBottom: { xs: 'none', md: '1px solid #EAF0F5' },
                bgcolor: { xs: 'transparent', md: '#fff' },
                display: {
                  xs: mobileStep === 'details' ? 'block' : 'none',
                  md: 'block',
                },
                width: '100%',
              }}
            >
              <Box data-demo-id="appointment-scheduler-client" ref={scheduleClientSearchRef} sx={{ position: 'relative', ...scheduleCenterPanelSx }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '10px',
                      bgcolor: '#E9F5FB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PersonRoundedIcon sx={{ fontSize: 18, color: '#2FA8D5' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={scheduleCenterTitleSx}>
                      {isClientLocked ? 'Client' : 'Select Client'}
                    </Typography>
                    <Typography sx={scheduleCenterSubtitleSx}>
                      {isClientLocked
                        ? 'This session is for the client below'
                        : 'Search and choose who this session is for'}
                    </Typography>
                  </Box>
                </Box>
                {!isClientLocked ? (
                  <TextField
                    inputProps={{ 'data-demo-id': 'appointment-scheduler-client-search' }}
                    fullWidth
                    value={scheduleClientSearch}
                    onChange={(event) => {
                      setScheduleClientSearch(event.target.value);
                      setIsScheduleClientListOpen(true);
                      setScheduleFieldErrors((current) => ({ ...current, client: undefined }));
                      setScheduleFormFeedback(null);
                    }}
                    onFocus={() => setIsScheduleClientListOpen(true)}
                    placeholder="Search Clients..."
                    sx={scheduleCenterSearchSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon sx={{ color: '#98A2B3', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : null}
                {(isClientLocked || (selectedClientForSchedule && !isScheduleClientListOpen)) ? (
                  <Box sx={{ ...(isClientLocked ? { mt: 0.75 } : {}), ...scheduleCenterSelectedClientSx }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '10px',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <PersonRoundedIcon sx={{ fontSize: 18, color: '#2FA8D5' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: '#1D2939',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <ClientNameLink clientId={scheduleClientLinkId} className="block truncate text-[#1D2939] hover:text-[#1D2939]">
                          {scheduleClientName}
                        </ClientNameLink>
                      </Typography>
                      {scheduleClientEmail ? (
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px',
                            color: '#667085',
                            lineHeight: 1.35,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {scheduleClientEmail}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : null}
                {!isClientLocked && isScheduleClientListOpen && (
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 0.5,
                      borderRadius: '10px',
                      border: '1px solid #e4e7ec',
                      bgcolor: '#fff',
                      boxShadow: '0 12px 28px rgba(16, 24, 40, 0.18)',
                      overflow: 'hidden',
                      position: { xs: 'relative', md: 'absolute' },
                      left: { md: 0 },
                      right: { md: 0 },
                      top: { md: '100%' },
                      zIndex: 10,
                    }}
                  >
                  <Box
                    sx={{
                      maxHeight: { xs: 240, md: 190 },
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-track': { backgroundColor: '#eef1f5', borderRadius: '999px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#26a9da', borderRadius: '999px' },
                    }}
                  >
                    {isScheduleClientSearchLoading ? (
                      <Box sx={{ px: 2, py: 2.5 }}>
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085' }}>
                          Searching clients...
                        </Typography>
                      </Box>
                    ) : filteredScheduleClients.length === 0 ? (
                      <Box sx={{ px: 2, py: 2.5 }}>
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085' }}>
                          No clients matched your search.
                        </Typography>
                      </Box>
                    ) : (
                      filteredScheduleClients.map((client, index) => {
                        const clientId = String(client.id);
                        const isSelected = selectedScheduleClientId === clientId;
                        const conditionSummary = getClientConditionSummary(client);

                        return (
                          <Box
                            key={clientId}
                            data-demo-id="appointment-scheduler-client-option"
                            onClick={() => {
                              setSelectedScheduleClientId(clientId);
                              setScheduleClientSearch('');
                              setIsScheduleClientListOpen(false);
                              setScheduleFieldErrors((current) => ({ ...current, client: undefined }));
                              setScheduleFormFeedback(null);
                            }}
                            sx={{
                              px: 1.5,
                              py: 1.15,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.25,
                              cursor: 'pointer',
                              bgcolor: isSelected ? '#e9f5fb' : '#fff',
                              borderBottom: index < filteredScheduleClients.length - 1 ? '1px solid #edf2f7' : '1px solid transparent',
                              '&:hover': { bgcolor: isSelected ? '#e2f3fb' : '#f8fbff' },
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                bgcolor: '#edf4fd',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <PersonRoundedIcon sx={{ fontSize: 18, color: '#4b91e3' }} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '0.84rem', lineHeight: 1.35, color: '#344054', wordBreak: 'break-word' }}>
                                {getClientDisplayName(client)}
                              </Typography>
                              {conditionSummary ? (
                                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', lineHeight: 1.35, color: '#98a2b3', wordBreak: 'break-word' }}>
                                  {conditionSummary}
                                </Typography>
                              ) : null}
                              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.35, color: '#c1c7d0', wordBreak: 'break-word' }}>
                                {client.email || 'No email provided'}
                                {getClientStatusLabel(client) ? (
                                  <>
                                    <Box component="span" sx={{ px: 0.4 }}>•</Box>
                                    {getClientStatusLabel(client)}
                                  </>
                                ) : null}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  </Paper>
                )}
                {scheduleFieldErrors.client ? (
                  <Typography sx={{ mt: 0.85, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#dc2626' }}>
                    {scheduleFieldErrors.client}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Box
              sx={{
                px: { xs: 1.75, md: 2.5 },
                pt: { xs: 1.5, md: 2 },
                pb: { xs: 0.5, md: 2 },
                minHeight: 0,
                overflowY: { xs: 'visible', md: 'auto' },
                display: {
                  xs: mobileStep === 'when' ? 'flex' : 'none',
                  md: 'flex',
                },
                flexDirection: 'column',
                gap: 1.25,
                flex: { xs: 'none', md: 1 },
                width: '100%',
              }}
            >
              <Box>
                <Typography sx={scheduleCenterTitleSx}>Select a Date</Typography>
                <Typography sx={scheduleCenterSubtitleSx}>Choose any available weekday below</Typography>
              </Box>
              <Box sx={scheduleCalendarPanelSx}>
              <Box data-demo-id="appointment-scheduler-calendar" sx={{ display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 40px', alignItems: 'center' }}>
                <IconButton
                  onClick={() => setScheduleMonth((value) => subMonths(value, 1))}
                  sx={{ width: 30, height: 30, color: '#98A2B3' }}
                >
                  <NavigateBeforeRoundedIcon />
                </IconButton>
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: '#1D2939',
                  }}
                >
                  {format(scheduleMonth, 'MMMM yyyy')}
                </Typography>
                <IconButton
                  onClick={() => setScheduleMonth((value) => addMonths(value, 1))}
                  sx={{ width: 30, height: 30, color: '#98A2B3' }}
                >
                  <NavigateNextRoundedIcon />
                </IconButton>
              </Box>

              <Box sx={{ mt: 1.8, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', columnGap: 0.35, rowGap: 1.25 }}>
                {weekDayLabels.map((label) => (
                  <Typography
                    key={label}
                    sx={{
                      textAlign: 'center',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.04em',
                      color: '#C2CAD7',
                    }}
                  >
                    {label}
                  </Typography>
                ))}

                {calendarDays.map((day) => {
                  const isCurrentMonth = isSameMonth(day, scheduleMonth);
                  const isOutsideCurrentMonth = !isCurrentMonth;
                  const isPastDay = isPastScheduleDay(day);
                  const isUnavailableDay =
                    !isOutsideCurrentMonth &&
                    !isPastDay &&
                    (scheduleAvailabilityLoading || !isDayAvailable(day));
                  const isSelected = selectedScheduleDate ? isSameDay(day, selectedScheduleDate) : false;

                  if (isPastDay) {
                    return (
                      <ScheduleCalendarDisabledDay
                        key={day.toISOString()}
                        day={day}
                        tooltip={schedulePastDayTooltip}
                      />
                    );
                  }

                  if (isUnavailableDay) {
                    return (
                      <ScheduleCalendarDisabledDay
                        key={day.toISOString()}
                        day={day}
                        tooltip={scheduleAvailabilityLoading ? scheduleLoadingDayTooltip : scheduleUnavailableDayTooltip}
                      />
                    );
                  }

                  return (
                    <Button
                      key={day.toISOString()}
                      disableElevation
                      onClick={() => handleDatePick(day)}
                      sx={{
                        minHeight: 40,
                        borderRadius: '10px',
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: isSelected ? 700 : 600,
                        fontSize: '1rem',
                        color: isOutsideCurrentMonth
                          ? '#D3DBE6'
                          : isSelected
                            ? '#fff'
                            : '#1D2939',
                        bgcolor: isOutsideCurrentMonth
                          ? 'transparent'
                          : isSelected
                            ? '#2FA8D5'
                            : 'transparent',
                        '&:hover': {
                          bgcolor: isOutsideCurrentMonth
                            ? 'transparent'
                            : isSelected
                              ? '#2FA8D5'
                              : '#F2F7FA',
                        },
                      }}
                    >
                      {format(day, 'd')}
                    </Button>
                  );
                })}
              </Box>
              {showAvailabilitySettingsAction ? (
                <Box
                  sx={{
                    mt: 1.5,
                    px: 1.25,
                    py: 1.1,
                    borderRadius: '12px',
                    border: '1px solid #D8EEF7',
                    bgcolor: '#F5FBFE',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 1, sm: 1.25 },
                    position: 'relative',
                    zIndex: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      lineHeight: 1.35,
                      color: '#475467',
                    }}
                  >
                    Add availability slots to make booking dates selectable.
                  </Typography>
                  <Button
                    type="button"
                    variant="text"
                    onClick={handleOpenAvailabilitySettings}
                    sx={{
                      minHeight: 30,
                      px: 1,
                      py: 0.25,
                      borderRadius: '8px',
                      flexShrink: 0,
                      alignSelf: { xs: 'flex-start', sm: 'center' },
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      color: '#1598C6',
                      '&:hover': {
                        bgcolor: '#E7F6FC',
                      },
                    }}
                  >
                    Go to Availability
                  </Button>
                </Box>
              ) : null}
              </Box>
            </Box>

            {renderTimesPanel('inline')}

            {scheduleFormFeedback ? (
              <Box sx={{ px: { xs: 1.75, md: 2.5 }, pb: 1.1 }}>
                <InlineFeedback
                  variant={scheduleFormFeedback.variant}
                  title={scheduleFormFeedback.title}
                  message={scheduleFormFeedback.message}
                />
              </Box>
            ) : null}
          </Box>

          {renderTimesPanel('sidebar')}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 1.1,
            borderTop: '1px solid #EEF2F6',
            bgcolor: '#fff',
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
          }}
        >
          <Button
            data-demo-id="appointment-scheduler-submit"
            variant="contained"
            disableElevation
            disabled={!canSubmitAppointment}
            onClick={() => void handleContinueAppointment()}
            sx={scheduleActionButtonSx(canSubmitAppointment)}
          >
            {isScheduleCreating
              ? isEditMode
                ? 'Updating…'
                : 'Creating…'
              : !selectedScheduleDate
                ? 'Select a date to continue'
                : !clientRequirementMet
                  ? 'Select a client to continue'
                  : !scheduleAppointmentTitle.trim()
                    ? 'Enter appointment name'
                    : selectedTimeSlot
                      ? isEditMode
                        ? 'Update Appointment'
                        : 'Create Appointment'
                      : 'Now pick a time slot ->'}
          </Button>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            position: 'relative',
            zIndex: 3,
            px: 2,
            pt: 1.35,
            borderTop: '1px solid #EEF2F6',
            bgcolor: '#fff',
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 -4px 16px rgba(16, 24, 40, 0.06)',
            pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {mobileStep === 'when' ? (
            <Button
              type="button"
              variant="outlined"
              onClick={() => setMobileStep('details')}
              sx={{
                minHeight: 48,
                minWidth: 96,
                borderRadius: '12px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#475467',
                borderColor: '#D0D5DD',
              }}
            >
              Back
            </Button>
          ) : null}
          {mobileStep === 'when' ? (
            <Button
              data-demo-id="appointment-scheduler-submit"
              variant="contained"
              disableElevation
              disabled={!canSubmitAppointment}
              onClick={() => void handleContinueAppointment()}
              sx={scheduleActionButtonCompactSx(canSubmitAppointment)}
            >
              {isScheduleCreating
                ? isEditMode
                  ? 'Updating…'
                  : 'Creating…'
                : !selectedScheduleDate
                  ? 'Select a date'
                  : !selectedTimeSlot
                    ? 'Select a time'
                    : isEditMode
                      ? 'Update Appointment'
                      : 'Create Appointment'}
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              disabled={!canContinueFromDetails}
              onClick={handleMobileContinue}
              sx={scheduleActionButtonCompactSx(canContinueFromDetails)}
            >
              {!clientRequirementMet
                ? 'Select a client'
                : !scheduleAppointmentTitle.trim()
                  ? 'Enter appointment name'
                  : 'Continue to date & time'}
            </Button>
          )}
        </Box>
      </Dialog>

      <InviteQuestionnaireModal
        key={appointmentIdToEdit ?? 'new-appointment'}
        open={isQuestionnaireModalOpen}
        onClose={() => setIsQuestionnaireModalOpen(false)}
        clientFirstName={questionnaireClientFirstName}
        clientLastName={questionnaireClientLastName}
        clientEmail={scheduleClientEmail}
        selectedQuestionnaireIds={selectedQuestionnaireIds}
        onAttach={handleAttachQuestionnaires}
        eyebrowText="Add Questionnaire"
        titleText="Add Questionnaire"
        helperText={
          isEditMode
            ? 'Select questionnaires to assign to this client when you save the rescheduled appointment.'
            : 'Select questionnaires now and they will be assigned to the client when you create the appointment.'
        }
        attachButtonText="Add Questionnaire"
      />

      <Dialog
        open={isAppointmentCreatedPopupOpen}
        onClose={handleCloseCreatedPopup}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 28px)', sm: 560 },
            maxWidth: 'calc(100vw - 34px)',
            borderRadius: '20px',
            overflow: 'hidden',
            m: 0,
            bgcolor: '#fff',
            boxShadow: '0 30px 70px rgba(16, 24, 40, 0.24)',
          },
        }}
      >
        <Box sx={{ px: { xs: 2.4, sm: 3 }, py: 1.7, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                bgcolor: '#EAF6FD',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2CA8D6',
              }}
            >
              <AccessTimeRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.05rem', fontWeight: 700, color: '#344054', lineHeight: 1.1 }}>
                Appointment Created
              </Typography>
              <Typography sx={{ mt: 0.22, fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3', lineHeight: 1.2 }}>
                {createdAppointmentMeta ? `${createdAppointmentMeta.clientName} · ${createdAppointmentMeta.dateLabel}` : ''}
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleCloseCreatedPopup}
            sx={{
              width: 32,
              height: 32,
              color: '#98A2B3',
              '&:hover': { bgcolor: '#F8FAFC' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 21 }} />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2.6, sm: 5 }, py: { xs: 3.2, sm: 4 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 68,
              height: 68,
              mx: 'auto',
              borderRadius: '999px',
              bgcolor: '#DDFBE8',
              border: '1px solid #A6F4C5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#18A565',
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 34 }} />
          </Box>

          <Typography
            sx={{
              mt: 2,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.4rem' },
              color: '#1D2939',
              lineHeight: 1.2,
            }}
          >
            Appointment Created
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              color: '#667085',
              lineHeight: 1.4,
            }}
          >
            Appointment has been successfully scheduled
          </Typography>
          <Typography
            sx={{
              mt: 0.06,
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              fontWeight: 600,
              color: '#2FA8D5',
            }}
          >
            for {createdAppointmentMeta?.dateLabel || format(new Date(), 'dd MMM yyyy')}.
          </Typography>

          <Box
            sx={{
              mt: 2,
              mx: 'auto',
              width: { xs: '100%', sm: 340 },
              maxWidth: '100%',
              borderRadius: '12px',
              border: '1px solid #E4E7EC',
              bgcolor: '#FCFDFD',
              px: 2,
              py: 1.45,
              textAlign: 'left',
              display: 'grid',
              gap: 0.85,
            }}
          >
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.96rem', color: '#98A2B3' }}>
              Name: <Box component="span" sx={{ color: '#475467', fontWeight: 600 }}>{createdAppointmentMeta?.titleLabel || '—'}</Box>
            </Typography>
            {createdAppointmentMeta?.descriptionLabel ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.96rem', color: '#98A2B3' }}>
                Description:{' '}
                <Box component="span" sx={{ color: '#475467', fontWeight: 600 }}>
                  {createdAppointmentMeta.descriptionLabel}
                </Box>
              </Typography>
            ) : null}
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.96rem', color: '#98A2B3' }}>
              Mode: <Box component="span" sx={{ color: '#475467', fontWeight: 600 }}>{createdAppointmentMeta?.modeLabel || 'In-Person'}</Box>
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.96rem', color: '#98A2B3' }}>
              Location: <Box component="span" sx={{ color: '#2FA8D5', fontWeight: 600 }}>{createdAppointmentMeta?.locationLabel || 'Clinic Address'}</Box>
            </Typography>
          </Box>
        </Box>
      </Dialog>

    </>
  );
};

export default ScheduleAppointmentDialog;
